import { toAuditDetailsFormData } from "@interface48/forms";
import {
  AddOrUpdateTrainingProgramDepartmentCommand,
  AddOrUpdateTrainingProgramExternalDetailsCommand,
  AddTrainingProgramCommand,
  TrainingProgramDto,
  UpdateTrainingProgramCommand,
} from "../../../../api";
import { sortTrainingProgramComponentsByNumber } from "../../utils";
import { TrainingProgramFormData } from "../models";

export const toTrainingProgramFormData = (trainingProgram?: TrainingProgramDto): TrainingProgramFormData => {
  if (trainingProgram) {
    const isExternal = !!trainingProgram.externalUrl;

    return {
      isExisting: true,
      id: trainingProgram.id,
      name: trainingProgram.name,
      description: trainingProgram.description,
      isExternal,
      externalUrl: isExternal ? trainingProgram.externalUrl : undefined,
      externalDurationMinutes: isExternal ? trainingProgram.durationMinutes : undefined,
      componentIds: !isExternal
        ? (trainingProgram.components
            .sort(sortTrainingProgramComponentsByNumber)
            .map(
              (component) => component.courseId ?? component.documentId ?? component.videoId ?? component.quizId,
            ) as string[])
        : undefined,
      durationMinutes: !isExternal ? trainingProgram.durationMinutes : undefined,
      emailRecipients: trainingProgram.emailRecipients?.join(", "),
      sortOrder: trainingProgram.sortOrder,
      departments: trainingProgram.departments.map((department) => ({
        department: department.department,
        departmentName: department.departmentName,
        positions: department.positions,
        positionNames: department.positionNames,
      })),
      hasDepartments: !!trainingProgram.departments.length,
      trainingScorecardId: trainingProgram.trainingScorecardId,
      trainingScorecardName: trainingProgram.trainingScorecardName,
      isEnabled: trainingProgram.isEnabled,
      incrementMajorVersion: false,
      majorVersion: trainingProgram.majorVersion,
      version: trainingProgram.version,
      created: toAuditDetailsFormData(trainingProgram.createdAt, trainingProgram.createdByUserName),
      lastModified: toAuditDetailsFormData(trainingProgram.lastModifiedAt, trainingProgram.lastModifiedByUserName),
    };
  } else {
    return {
      isExisting: false,
      isExternal: false,
      isEnabled: false,
      incrementMajorVersion: false,
      componentIds: [undefined],
      hasDepartments: false,
      created: {},
      lastModified: {},
    };
  }
};

export const toAddTrainingProgramCommand = (formData: TrainingProgramFormData): AddTrainingProgramCommand => {
  const { name, description, isExternal, departments, trainingScorecardId, isEnabled, sortOrder } = formData;

  if (name == null || description == null) {
    throw new Error("One or more required fields undefined.");
  }

  let externalDetails: AddOrUpdateTrainingProgramExternalDetailsCommand | undefined = undefined;
  let componentIds: Array<string> | undefined = undefined;

  if (isExternal) {
    if (formData.externalUrl == null || formData.externalDurationMinutes == null) {
      throw new Error("One or more required fields undefined.");
    }

    externalDetails = {
      url: formData.externalUrl,
      durationMinutes: formData.externalDurationMinutes,
    };
  } else {
    if (formData.componentIds == null) {
      throw new Error("One or more required fields undefined.");
    }

    componentIds = formData.componentIds.map((componentId) => {
      if (componentId == null) {
        throw new Error("Component ID undefined.");
      }

      return componentId;
    });
  }

  const addOrUpdateTrainingProgramDepartmentCommands = departments?.map(
    (department): AddOrUpdateTrainingProgramDepartmentCommand => {
      if (department.department == null || department.positions == null) {
        throw new Error("Department or Positions undefined.");
      }

      return {
        department: department.department,
        positions: department.positions,
      };
    },
  );

  const emailRecipients = formData.emailRecipients
    ? formData.emailRecipients.split(",").map((r) => r.trim())
    : undefined;

  return {
    name,
    description,
    externalDetails,
    componentIds,
    departments: addOrUpdateTrainingProgramDepartmentCommands,
    trainingScorecardId,
    emailRecipients,
    isEnabled,
    sortOrder,
  };
};

export const toUpdateTrainingProgramCommand = (formData: TrainingProgramFormData): UpdateTrainingProgramCommand => {
  const { incrementMajorVersion } = formData;

  if (incrementMajorVersion == null) {
    throw new Error("One or more required fields undefined.");
  }

  const updateTrainingProgramCommand: UpdateTrainingProgramCommand = {
    ...toAddTrainingProgramCommand(formData),
    incrementMajorVersion,
  } as UpdateTrainingProgramCommand;

  return updateTrainingProgramCommand;
};
